// (c) chaehongee. Confidential and proprietary.

import { useState, useEffect, useCallback } from 'react'
import { updateUserPreferences } from '../services/user'
import { useAuth } from '../store/hooks/useAuth'

export type Theme = 'system' | 'light' | 'dark'

const STORAGE_KEY = 'knoll_theme'

function getStoredTheme(): Theme {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored === 'light' || stored === 'dark' || stored === 'system') return stored
    return 'system'
}

function applyTheme(theme: Theme) {
    const root = document.documentElement
    if (theme === 'system') {
        root.removeAttribute('data-theme')
    } else {
        root.setAttribute('data-theme', theme)
    }
}

// Apply stored theme immediately on module load to prevent flash
applyTheme(getStoredTheme())

export function useTheme() {
    const [theme, setThemeState] = useState<Theme>(getStoredTheme)
    const { user } = useAuth()

    useEffect(() => {
        applyTheme(theme)
    }, [theme])

    const setTheme = useCallback(
        async (newTheme: Theme) => {
            if (newTheme === theme) return

            setThemeState(newTheme)
            localStorage.setItem(STORAGE_KEY, newTheme)
            applyTheme(newTheme)

            if (user) {
                try {
                    await updateUserPreferences({ theme: newTheme })
                } catch (err) {
                    console.error('Failed to persist theme preference:', err)
                }
            }
        },
        [theme, user]
    )

    const hydrateTheme = useCallback((serverTheme: string | null | undefined) => {
        if (serverTheme && (serverTheme === 'light' || serverTheme === 'dark' || serverTheme === 'system')) {
            setThemeState(serverTheme as Theme)
            localStorage.setItem(STORAGE_KEY, serverTheme)
            applyTheme(serverTheme as Theme)
        }
    }, [])

    return { theme, setTheme, hydrateTheme }
}
